import React, { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { Button, MenuSection, MessageBanner, Pane, } from "@folio/stripes/components";
import { StripesConnectedSource, } from "@folio/stripes/smart-components";
import { useIntl } from "react-intl";
import TableComponent from "../../components/TableComponent";
import NavComponent from "../../components/NavComponent";
import { fetchAresUpdates, fetchAresUpdatesData } from "../../api/aresService";
import { useLatestPublisherPrefixes } from "../../hooks/useLatestPublisherPrefixes";
import { usePermissions } from "../../hooks/usePermissions";
import { usePageSize } from "../../hooks/usePageSize";
import AresUpdatesDetail from "./AresUpdatesDetail";
import { RecekPaneSet } from "../../components/RecekPaneSet";
import { formatDate } from "../../utils/dateTimeUtil";
import { DataListPane } from "../../components/DataListPane";
/**
 * Maps a sortable column id to the CQL field used by the
 * `ares-update-requests` endpoint. Only these three columns are sortable.
 */
var SORT_FIELD_MAP = {
    publisher: "publisher.name",
    createTime: "createTime",
    companyNumber: "publisher.companyNumber",
};
var buildSortBy = function (sort) {
    if (!sort.column) {
        return undefined;
    }
    var field = SORT_FIELD_MAP[sort.column];
    if (!field) {
        return undefined;
    }
    return sort.direction === "descending"
        ? "".concat(field, "/sort.descending")
        : field;
};
var AresUpdatesList = function () {
    var history = useHistory();
    var ky = useOkapiKy();
    var canEditAresUpdates = usePermissions().canEditAresUpdates;
    var formatMessage = useIntl().formatMessage;
    var _a = useState(null), selectedAresUpdate = _a[0], setSelectedAresUpdate = _a[1];
    var _b = useState(true), filterPaneIsVisible = _b[0], setFilterPaneIsVisible = _b[1];
    var _c = useState(0), totalRecords = _c[0], setTotalRecords = _c[1];
    var _d = useState(0), pageOffset = _d[0], setPageOffset = _d[1];
    var _e = useState({ direction: "ascending" }), sort = _e[0], setSort = _e[1];
    var _f = usePageSize(function () {
        return setPageOffset(0);
    }), pageSize = _f.pageSize, handlePageSizeChange = _f.handlePageSizeChange;
    useEffect(function () {
        var queryParams = new URLSearchParams(location.search);
        var aresId = queryParams.get("detail");
        if (aresId) {
            fetchAresUpdatesData(aresId, ky).then(function (ares) {
                if (ares) {
                    setSelectedAresUpdate(ares);
                    // Ensure filters are preserved when opening detail
                    history.replace({ search: "?".concat(queryParams.toString()) });
                }
            });
        }
    }, [location.search]);
    var sortBy = buildSortBy(sort);
    var _g = useQuery(["ares-update-requests", pageOffset, pageSize, sortBy], function () {
        return fetchAresUpdates(ky, {
            offset: pageOffset,
            limit: pageSize,
            sortBy: sortBy,
        });
    }, {
        enabled: true,
        keepPreviousData: true,
        onSuccess: function (data) {
            setTotalRecords(data.totalRecords);
        },
    }), data = _g.data, isLoading = _g.isLoading, isError = _g.isError;
    var aresUpdatesData = useMemo(function () { var _a; return (_a = data === null || data === void 0 ? void 0 : data.aresUpdateRequests) !== null && _a !== void 0 ? _a : []; }, [data]);
    // Latest assigned prefix per publisher for the rows currently shown: one
    // request per table page (DB-889).
    var _h = useLatestPublisherPrefixes(ky, aresUpdatesData), prefixByPublisher = _h.prefixByPublisher, prefixesFailed = _h.isError;
    var fetchMoreData = function (offset) {
        setPageOffset(offset);
    };
    var handleHeaderClick = function (columnId) {
        if (!SORT_FIELD_MAP[columnId]) {
            return;
        }
        setPageOffset(0);
        setSort(function (prev) {
            return prev.column === columnId
                ? {
                    column: columnId,
                    direction: prev.direction === "ascending" ? "descending" : "ascending",
                }
                : { column: columnId, direction: "ascending" };
        });
    };
    var columns = useMemo(function () { return [
        {
            name: formatMessage({
                id: "ui-br-publishers.publishers.table.columns.name",
            }),
            key: "publisher",
            id: "publisher",
            sortable: true,
            cellRenderer: function (item) { var _a; return ((_a = item.publisher) === null || _a === void 0 ? void 0 : _a.name) || "N/A"; },
            clickable: true,
            onClick: function (item) {
                var currentParams = new URLSearchParams(location.search);
                currentParams.delete("detail");
                history.push({
                    search: "".concat(currentParams.toString(), "&detail=").concat(item.id),
                });
                setSelectedAresUpdate(item);
            },
        },
        {
            name: formatMessage({
                id: "ui-br-publishers.publishers.table.columns.companyNumber",
            }),
            key: "publisher",
            id: "companyNumber",
            sortable: true,
            cellRenderer: function (item) { var _a; return ((_a = item.publisher) === null || _a === void 0 ? void 0 : _a.companyNumber) || ""; },
        },
        {
            name: formatMessage({
                id: "ui-br-publishers.publishers.table.columns.createTime",
            }),
            key: "createTime",
            id: "createTime",
            sortable: true,
            cellRenderer: function (item) {
                return item.createTime ? formatDate(item.createTime) : "";
            },
        },
        {
            name: formatMessage({
                id: "ui-br-publishers.ares.table.columns.lastPrefix",
            }),
            key: "data",
            id: "lastPrefix",
            sortable: false,
            cellRenderer: function (item) { var _a; return ((_a = item.publisher) === null || _a === void 0 ? void 0 : _a.id) ? prefixByPublisher[item.publisher.id] || "" : ""; },
        },
    ]; }, [history, formatMessage, prefixByPublisher]);
    var toggleFilterPane = function () { return setFilterPaneIsVisible(function (prev) { return !prev; }); };
    var source = new StripesConnectedSource({}, ky);
    source.update = function () { };
    source.records = function () { return aresUpdatesData; };
    source.resultCount = function () { return totalRecords; };
    source.totalCount = function () { return totalRecords; };
    source.pending = function () { return isLoading; };
    source.loaded = function () { return true; };
    source.failure = function () { return (isError ? { message: "Error loading data" } : null); };
    source.failureMessage = function () { return "Error loading data"; };
    source.successfulMutations = function () { return []; };
    source.fetchMore = function () { return fetchMoreData; };
    return (React.createElement(RecekPaneSet, null,
        filterPaneIsVisible && (React.createElement(Pane, { id: "menuPane", defaultWidth: "25%", paneTitle: formatMessage({
                id: "ui-br-publishers.pane.title.navigation",
            }) },
            React.createElement("div", { style: { display: "flex", flexDirection: "column" } },
                React.createElement(NavComponent, null)))),
        React.createElement(DataListPane, { paneId: "contentPane", paneTitle: formatMessage({
                id: "ui-br-publishers.ares.update.pane.title",
            }), paneSub: formatMessage({ id: "ui-br-publishers.publishers.table.subtitle.records-found" }, { count: totalRecords }), filterPaneIsVisible: filterPaneIsVisible, toggleFilterPane: toggleFilterPane },
            prefixesFailed && (React.createElement(MessageBanner, { type: "error" }, formatMessage({
                id: "ui-br-publishers.ares.table.lastPrefix.loadError",
            }))),
            React.createElement(TableComponent, { data: aresUpdatesData, columns: columns, pageSize: pageSize, onPageSizeChange: handlePageSizeChange, totalCount: totalRecords, pagingOffset: pageOffset, filterPaneIsVisible: filterPaneIsVisible, onNeedMoreData: function (offset) {
                    fetchMoreData(offset);
                }, toggleFilterPane: function () { return setFilterPaneIsVisible(function (prev) { return !prev; }); }, source: source, pagingType: "prev-next", sortedColumn: sort.column, sortDirection: sort.column ? sort.direction : undefined, onHeaderClick: handleHeaderClick })),
        selectedAresUpdate && (React.createElement(Pane, { id: "detailPane", defaultWidth: "35%", paneTitle: formatMessage({
                id: "ui-br-publishers.ares.updates.detail.pane.title",
            }), onClose: function () {
                setSelectedAresUpdate(null);
                var queryParams = new URLSearchParams(location.search);
                queryParams.delete("detail");
                history.replace({ search: queryParams.toString() });
            }, dismissible: true, actionMenu: function () { return (React.createElement(MenuSection, { label: formatMessage({ id: "ui-br-publishers.menu.label.actions" }), id: "actions-menu" }, canEditAresUpdates && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                    return history.push("/brpublishers/ares-updates/edit/".concat(selectedAresUpdate.id));
                }, fullWidth: true }, formatMessage({ id: "ui-br-publishers.button.change" }))))); } }, selectedAresUpdate.publisher ? (React.createElement(AresUpdatesDetail, { ares: selectedAresUpdate })) : (React.createElement("div", null, "No details available"))))));
};
export default AresUpdatesList;
